import type { Theme } from '../../../theme';

export type LangKey = 'en' | 'es';

export interface LanguageTabsProps {
  value: LangKey;
  onChange: (lang: LangKey) => void;
  options: { value: LangKey; label: string }[];
  theme: Theme;
  /** Acessibilidade: use a chave i18n do contexto (ex.: courses.create.module_name_language_aria) */
  'aria-label'?: string;
}

/**
 * Segmented control para seleção de idioma (EN/ES).
 * Aba ativa: fundo amarelo claro, borda amarela. Inativa: fundo cinza, borda transparente.
 */
export function LanguageTabs({
  value,
  onChange,
  options,
  theme,
  'aria-label': ariaLabel,
}: LanguageTabsProps) {
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    width: '100%',
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius.sm2,
    padding: theme.spacing.sm,
    overflow: 'hidden',
    gap: 0,
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      style={containerStyle}
    >
      {options.map((opt, index) => {
        const isActive = value === opt.value;
        const isFirst = index === 0;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            style={{
              flex: 1,
              padding: `${theme.spacing.xs} ${theme.spacing.sm2}`,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.fontSize.xs,
              fontWeight: theme.typography.fontWeight.semibold,
              lineHeight: 1.25,
              color: isActive ? theme.palette.text.primary : theme.palette.text.secondary,
              backgroundColor: isActive
                ? theme.palette.background.paper
                : theme.palette.background.default,
              border: isActive
                ? `1px solid ${theme.palette.primary.main}`
                : '1px solid transparent',
              borderRadius: isActive
                ? theme.shape.borderRadius.sm2
                : isFirst
                  ? `${theme.shape.borderRadius.sm2} 0 0 ${theme.shape.borderRadius.sm2}`
                  : `0 ${theme.shape.borderRadius.sm2} ${theme.shape.borderRadius.sm2} 0`,
              cursor: 'pointer',
              textAlign: 'center',
            }}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
